/**
 * Moteur commun — solides usuels en perspective cavalière, arêtes cachées en
 * pointillés (dupliqué depuis gen_1019, 6e « reconnaître les solides », pour
 * ne pas toucher à un gen validé ; gen 1068 « cube, pavé en perspective »).
 *
 * parts : {t:'poly',pts,fill} | {t:'line',a,b,dash} | {t:'ellipse',cx,cy,rx,ry}
 *         {t:'circle',cx,cy,r} | {t:'arc',x1,y1,x2,y2,rx,ry,sweep,dash}
 * Vues canoniques : le cube a une face avant CARRÉE et une fuyante réduite,
 * le pavé droit non cube a par construction une face avant NON carrée.
 */
import { randomChoice, randInt } from '../../../utils';

export const BLEU = '#1E3A8A';
export const FACE1 = '#eff6ff';
export const FACE2 = '#dbeafe';

/** Le pavé/cube générique : face avant w×h, fuyante (dx, dy vers le haut). */
const boiteParts = (w, h, dx, dy) => {
    const fTL = [0, 0]; const fTR = [w, 0]; const fBR = [w, h]; const fBL = [0, h];
    const add = (P) => [P[0] + dx, P[1] + dy];
    const droite = dx > 0;
    const cache = droite ? add(fBL) : add(fBR);   // le sommet arrière invisible
    return [
        { t: 'poly', pts: [fTL, fTR, add(fTR), add(fTL)], fill: FACE2 },
        { t: 'poly', pts: droite ? [fTR, fBR, add(fBR), add(fTR)] : [fTL, fBL, add(fBL), add(fTL)], fill: FACE2 },
        { t: 'poly', pts: [fTL, fTR, fBR, fBL], fill: FACE1 },
        { t: 'line', a: cache, b: droite ? fBL : fBR, dash: true },
        { t: 'line', a: cache, b: droite ? add(fBR) : add(fBL), dash: true },
        { t: 'line', a: cache, b: droite ? add(fTL) : add(fTR), dash: true },
    ];
};

export const SOLIDES = {
    cube: () => {
        const a = randInt(42, 56);
        const k = 0.42 + Math.random() * 0.1;
        const sx = randomChoice([1, -1]);
        return boiteParts(a, a, a * k * 1.15 * sx, -a * k);
    },
    pave: () => {
        const h = randInt(32, 42);
        const w = Math.round(h * (1.45 + Math.random() * 0.4));
        const p = randInt(24, 32);
        const sx = randomChoice([1, -1]);
        return boiteParts(w, h, p * sx, -p * 0.8);
    },
    pyramide: () => {
        const b = randInt(48, 62);
        const H = Math.round(b * (0.85 + Math.random() * 0.3));
        const sx = randomChoice([1, -1]);
        const dx = sx * Math.round(b * (0.35 + Math.random() * 0.12));
        const dy = -Math.round(b * 0.3);
        const fL = [0, 0]; const fR = [b, 0];
        const bNear = sx > 0 ? [b + dx, dy] : [dx, dy];
        const bFar = sx > 0 ? [dx, dy] : [b + dx, dy];
        const cx = (fL[0] + fR[0] + b + 2 * dx) / 4;
        const S = [cx, dy / 2 - H];
        return [
            { t: 'poly', pts: [S, fL, fR], fill: FACE1 },
            { t: 'poly', pts: sx > 0 ? [S, fR, bNear] : [S, fL, bNear], fill: FACE2 },
            { t: 'line', a: bFar, b: bNear, dash: true },
            { t: 'line', a: bFar, b: sx > 0 ? fL : fR, dash: true },
            { t: 'line', a: S, b: bFar, dash: true },
        ];
    },
    prisme: () => {
        const bt = randInt(40, 54);
        const ht = Math.round(bt * (0.7 + Math.random() * 0.3));
        const prof = randInt(30, 42);
        const sx = randomChoice([1, -1]);
        const o = [sx * prof * 0.85, -prof * 0.55];
        const T = [bt / 2, 0]; const BL = [0, ht]; const BR = [bt, ht];
        const add = (P) => [P[0] + o[0], P[1] + o[1]];
        const near = sx > 0 ? BR : BL; const far = sx > 0 ? BL : BR;
        return [
            { t: 'poly', pts: [T, near, add(near), add(T)], fill: FACE2 },
            { t: 'poly', pts: [T, BL, BR], fill: FACE1 },
            { t: 'line', a: far, b: add(far), dash: true },
            { t: 'line', a: add(far), b: add(T), dash: true },
            { t: 'line', a: add(far), b: add(near), dash: true },
        ];
    },
    cylindre: () => {
        const rx = randInt(20, 28);
        const H = Math.round(rx * (1.5 + Math.random() * 1.0));
        const ry = Math.max(6, Math.round(rx * 0.32));
        const cyB = ry + H;
        return [
            { t: 'line', a: [0, ry], b: [0, cyB], dash: false },
            { t: 'line', a: [2 * rx, ry], b: [2 * rx, cyB], dash: false },
            { t: 'arc', x1: 0, y1: cyB, x2: 2 * rx, y2: cyB, rx, ry, sweep: 0, dash: false },
            { t: 'arc', x1: 0, y1: cyB, x2: 2 * rx, y2: cyB, rx, ry, sweep: 1, dash: true },
            { t: 'ellipse', cx: rx, cy: ry, rx, ry },
        ];
    },
    cone: () => {
        const rx = randInt(24, 32);
        const H = Math.round(rx * (1.7 + Math.random() * 0.8));
        const ry = Math.max(7, Math.round(rx * 0.3));
        return [
            { t: 'line', a: [rx, 0], b: [0, H], dash: false },
            { t: 'line', a: [rx, 0], b: [2 * rx, H], dash: false },
            { t: 'arc', x1: 0, y1: H, x2: 2 * rx, y2: H, rx, ry, sweep: 0, dash: false },
            { t: 'arc', x1: 0, y1: H, x2: 2 * rx, y2: H, rx, ry, sweep: 1, dash: true },
        ];
    },
    boule: () => {
        const R = randInt(28, 36);
        const ry = Math.round(R * 0.34);
        return [
            { t: 'circle', cx: R, cy: R, r: R },
            { t: 'arc', x1: 0, y1: R, x2: 2 * R, y2: R, rx: R, ry, sweep: 0, dash: false },
            { t: 'arc', x1: 0, y1: R, x2: 2 * R, y2: R, rx: R, ry, sweep: 1, dash: true },
        ];
    },
};

/** Bbox des primitives (les arcs comptent leur flèche d'ellipse). */
export const bboxDe = (parts) => {
    const xs = []; const ys = [];
    for (const p of parts) {
        if (p.t === 'poly') p.pts.forEach(([x, y]) => { xs.push(x); ys.push(y); });
        else if (p.t === 'line') { xs.push(p.a[0], p.b[0]); ys.push(p.a[1], p.b[1]); }
        else if (p.t === 'ellipse') { xs.push(p.cx - p.rx, p.cx + p.rx); ys.push(p.cy - p.ry, p.cy + p.ry); }
        else if (p.t === 'circle') { xs.push(p.cx - p.r, p.cx + p.r); ys.push(p.cy - p.r, p.cy + p.r); }
        else if (p.t === 'arc') { xs.push(p.x1, p.x2); ys.push(p.y1 - p.ry, p.y1 + p.ry, p.y2 - p.ry, p.y2 + p.ry); }
    }
    return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
};

/** Émission SVG : translation (x0, y0) + échelle s, pointillés pour le caché. */
export const emettre = (parts, x0, y0, s = 1) => {
    const X = (x) => (x0 + x * s).toFixed(1);
    const Y = (y) => (y0 + y * s).toFixed(1);
    const trait = (dash) => `stroke="${BLEU}" stroke-width="${dash ? 1.5 : 2}" stroke-linejoin="round" stroke-linecap="round"${dash ? ' stroke-dasharray="5 4"' : ''}`;
    const out = [];
    for (const p of parts) {
        if (p.t === 'poly') out.push(`<polygon points="${p.pts.map(([x, y]) => `${X(x)},${Y(y)}`).join(' ')}" fill="${p.fill}" ${trait(false)}/>`);
        else if (p.t === 'line') out.push(`<line x1="${X(p.a[0])}" y1="${Y(p.a[1])}" x2="${X(p.b[0])}" y2="${Y(p.b[1])}" ${trait(p.dash)}/>`);
        else if (p.t === 'ellipse') out.push(`<ellipse cx="${X(p.cx)}" cy="${Y(p.cy)}" rx="${(p.rx * s).toFixed(1)}" ry="${(p.ry * s).toFixed(1)}" fill="${FACE2}" ${trait(false)}/>`);
        else if (p.t === 'circle') out.push(`<circle cx="${X(p.cx)}" cy="${Y(p.cy)}" r="${(p.r * s).toFixed(1)}" fill="${FACE1}" ${trait(false)}/>`);
        else if (p.t === 'arc') out.push(`<path d="M ${X(p.x1)} ${Y(p.y1)} A ${(p.rx * s).toFixed(1)} ${(p.ry * s).toFixed(1)} 0 0 ${p.sweep} ${X(p.x2)} ${Y(p.y2)}" fill="none" ${trait(p.dash)}/>`);
    }
    return out;
};

/** Un solide posé dans une case : normalisé, centré, mis à l'échelle. Renvoie les lignes SVG. */
export const poserSolide = (parts, cx, cy, maxW, maxH) => {
    const bb = bboxDe(parts);
    const w = bb.maxX - bb.minX; const h = bb.maxY - bb.minY;
    const s = Math.min(1, maxW / w, maxH / h);
    const x0 = cx - (w * s) / 2 - bb.minX * s;
    const y0 = cy - (h * s) / 2 - bb.minY * s;
    return emettre(parts, x0, y0, s);
};

/** SVG d'un seul solide, en vraie taille avec une marge. */
export const svgSolide = (parts, extra = '') => {
    const bb = bboxDe(parts);
    const W = Math.ceil(bb.maxX - bb.minX) + 28; const H = Math.ceil(bb.maxY - bb.minY) + 28;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${Math.min(220, Math.round(W * 1.6))}" height="${Math.min(220, Math.round(W * 1.6)) * H / W}" font-family="Arial"${extra}>${emettre(parts, 14 - bb.minX, 14 - bb.minY).join('')}</svg>`;
};
